import React, { useState, useRef } from "react";
import Barcode from "react-barcode";
import type { BasketItem } from "../page";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";
import { amountInWordsTaka } from "../../../utils/numberToWords";

interface ReturnReceiptModalProps {
  onClose: () => void;
  basketItems: BasketItem[];
  subtotal: number;
  // Item discounts + prorated order discount of the returned lines
  discount: number;
  tax: number;
  // Amount refunded in cash, or credited to the credit memo on an exchange
  total: number;
  isRefund: boolean;
  receiptData?: {
    receiptNo?: string;
    creditMemoNumber?: string;
    saleDate?: string;
    cashier?: string;
    customerName?: string;
    customerMobile?: string | null;
    binNumber?: string | null;
  };
}

// Same layout as PrintReceiptModal (sale receipt), with a refund / credit memo
// section in place of the payment section.
export default function ReturnReceiptModal({
  onClose,
  basketItems,
  subtotal,
  discount,
  tax,
  total,
  isRefund,
  receiptData,
}: ReturnReceiptModalProps) {
  const [isPrinting, setIsPrinting] = useState(false);
  const receiptPreviewRef = useRef<HTMLDivElement>(null);

  const businessDetails = (() => {
    try { return JSON.parse(localStorage.getItem("businessDetails") || "{}"); } catch { return {}; }
  })();
  const bizData = businessDetails?.data ?? businessDetails;
  const logoUrl = bizData?.imagePath ?? null;
  const bizPhone = bizData?.phoneNumber ?? null;
  const bizAddress = (() => {
    const a = bizData?.addressDetail;
    if (!a) return "";
    return [a.addressLine1, a.addressLine2, a.city, a.district, a.division, a.country]
      .map((p) => (p && typeof p === "object" ? p.name : p))
      .filter((p) => p && p !== "string" && p !== "null")
      .join(", ");
  })();
  const outlet = (() => {
    try { return JSON.parse(localStorage.getItem("outlet") || "{}"); } catch { return {}; }
  })();
  const counterInfo = JSON.parse(localStorage.getItem("counterCheckedInInfo") || "{}");
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");

  const effectiveDate = receiptData?.saleDate ? new Date(receiptData.saleDate) : new Date();
  const returnNo = receiptData?.receiptNo ?? "";
  const creditMemoNo = receiptData?.creditMemoNumber ?? "";
  const dateShort = effectiveDate
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    .replace(/ /g, "-");
  const timeStr = effectiveDate.toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const cashier = receiptData?.cashier || userInfo?.name || userInfo?.email || "Cashier";
  const customerName = receiptData?.customerName || "Walk In Customer";
  const customerMobile = receiptData?.customerMobile || "-";
  const binNumber = receiptData?.binNumber ?? null;
  const outletName = counterInfo?.outletName ?? "";
  const storeAddress = bizAddress || outlet?.address || outlet?.location || "";
  const storePhone = bizPhone ?? outlet?.phone ?? outlet?.phoneNumber ?? outlet?.contactNumber ?? null;
  const totalQty = basketItems.reduce((s, i) => s + Number(i.qty || 0), 0);
  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const title = isRefund ? "Refund Receipt" : "Credit Memo";
  const infoTitle = isRefund ? "Refund Info" : "Credit Info";
  const infoLabel = isRefund ? "Cash Refund" : "Credit Memo";
  const watermark = isRefund ? "REFUNDED" : null;
  const terms = [
    "1. Products exchangeable within 15 days of purchase.",
    "2. Exchanged products cannot be re-exchanged.",
    "3. No refund in any form is allowed.",
  ];

  // Pure black — thermal printers only support black
  const BLK = "color:#000";

  const buildReceiptHtml = () => {
    const itemsHtml = basketItems
      .map(
        (item, idx) => `
        <tr>
          <td style="padding:2px 2px 2px 0;vertical-align:top;font-size:10px;${BLK}">${idx + 1}</td>
          <td style="padding:2px 2px;vertical-align:top;${BLK}">
            <div style="font-size:10px;${BLK}">${item.name}</div>
            ${item.code ? `<div style="font-size:9px;${BLK}">${item.code}</div>` : ""}
          </td>
          <td style="text-align:center;padding:2px 2px;vertical-align:top;font-size:10px;${BLK}">${item.qty.toFixed(3)}</td>
          <td style="text-align:right;white-space:nowrap;padding:2px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.price)}</td>
          <td style="text-align:right;white-space:nowrap;padding:2px 0 2px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.total)}</td>
        </tr>`,
      )
      .join("");

    const watermarkHtml = watermark
      ? `<div class="wm" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);font-size:30px;font-weight:900;white-space:nowrap;pointer-events:none;z-index:0;letter-spacing:2px">${watermark}</div>`
      : "";

    const exchangeHtml = isRefund
      ? ""
      : `<div style="border-bottom:1px solid #999;margin:4px 0 2px"></div>
        <div style="font-size:10px;font-weight:bold;margin-bottom:1px;${BLK}">Terms &amp; Conditions:</div>
        ${terms.map((t) => `<div style="font-size:9px;${BLK}">${t}</div>`).join("")}
        <div style="text-align:center;margin:6px 0 2px">
          <canvas class="barcode-receipt"></canvas>
          <div style="font-size:9px;margin-top:2px;${BLK}">${creditMemoNo}</div>
        </div>`;

    return `
      <div style="position:relative;overflow:hidden;font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px;line-height:1.3;max-width:300px;padding:0 8px;${BLK}">
        ${watermarkHtml}

        <div style="text-align:center;margin:0;padding:0">
          <div style="display:flex;align-items:center;justify-content:center">
            <img src="${logoUrl ?? `${window.location.origin}/biznify-logo.png`}" alt="Biznify" style="max-width:100%;max-height:80px;width:auto;height:auto;object-fit:contain;display:block" />
          </div>
          ${outletName ? `<div style="font-size:11px;font-weight:bold;margin-top:2px;${BLK}">${outletName}</div>` : ""}
          ${storeAddress ? `<div style="font-size:9px;font-weight:bold;${BLK}">${storeAddress}</div>` : ""}
          ${storePhone ? `<div style="font-size:9px;${BLK}">Phone : ${storePhone}</div>` : ""}
          <div style="font-size:12px;font-weight:bold;margin-top:3px;${BLK}">${title}</div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:9px;margin-top:2px;${BLK}">
          <tr>
            <td style="${BLK}">${binNumber ? `BIN : ${binNumber}` : ""}</td>
          </tr>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:10px;${BLK}">
          <tr><td colspan="2" style="font-weight:bold;${BLK}">Return No. : ${returnNo}</td></tr>
          ${!isRefund ? `<tr><td colspan="2" style="font-weight:bold;${BLK}">Credit Memo No. : ${creditMemoNo}</td></tr>` : ""}
          <tr>
            <td style="font-weight:bold;${BLK}">Return Date : ${dateShort}</td>
            <td style="text-align:right;white-space:nowrap;font-weight:bold;${BLK}">${timeStr}</td>
          </tr>
          <tr><td colspan="2" style="${BLK}">Name : ${customerName}</td></tr>
          <tr><td colspan="2" style="${BLK}">Mobile : ${customerMobile}</td></tr>
          <tr><td colspan="2" style="font-weight:bold;${BLK}">Served by : ${cashier}</td></tr>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;table-layout:fixed;${BLK}">
          <colgroup>
            <col style="width:16px"/>
            <col/>
            <col style="width:36px"/>
            <col style="width:52px"/>
            <col style="width:56px"/>
          </colgroup>
          <thead>
            <tr style="border-bottom:1px solid #000">
              <th style="text-align:left;padding:2px 2px 2px 0;font-size:10px;font-weight:bold;${BLK}">SL</th>
              <th style="text-align:left;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">Description</th>
              <th style="text-align:center;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">Rqty</th>
              <th style="text-align:right;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">MRP</th>
              <th style="text-align:right;padding:2px 0 2px 2px;font-size:10px;font-weight:bold;${BLK}">Amount</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
          <tfoot>
            <tr style="border-top:1px solid #000">
              <td colspan="2" style="padding:2px 2px 2px 0;font-size:10px;font-weight:bold;${BLK}">Sub Total :</td>
              <td style="text-align:center;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">${totalQty.toFixed(2)}</td>
              <td></td>
              <td style="text-align:right;padding:2px 0 2px 2px;font-size:10px;font-weight:bold;${BLK}">${fmt(subtotal)}</td>
            </tr>
          </tfoot>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Discount :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(discount)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">VAT :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(tax)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;font-weight:bold;${BLK}">Net Amount :</td><td style="text-align:right;padding:1px 0;font-weight:bold;${BLK}">${fmt(total)}</td></tr>
        </table>

        <div style="font-size:10px;margin:2px 0;${BLK}"><b>In words:</b> <i>${amountInWordsTaka(total)}</i></div>

        <div style="display:inline-block;font-size:11px;font-weight:bold;margin:2px 0 1px;padding-bottom:1px;border-bottom:1px solid #000;${BLK}">${infoTitle}</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr style="border-bottom:1px solid #000">
            <td style="padding:1px 0;${BLK}">DESCRIPTION</td>
            <td style="text-align:right;padding:1px 0;${BLK}">AMOUNT</td>
          </tr>
          <tr><td style="padding:1px 0;font-weight:bold;font-size:11px;${BLK}">${infoLabel}</td><td style="text-align:right;padding:1px 0;font-weight:bold;font-size:11px;${BLK}">${fmt(total)}</td></tr>
        </table>
        ${exchangeHtml}

        <div style="text-align:center;font-size:10px;margin-top:4px;line-height:1.4;${BLK}">
          <div style="font-size:11px;font-weight:bold;${BLK}">Thank you for shopping</div>
          <div style="font-size:10px;font-weight:bold;${BLK}">Customer Copy</div>
        </div>
        <div style="text-align:right;font-size:9px;margin-top:4px;${BLK}">Powered by biznify.net</div>
      </div>`;
  };

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptPreviewRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error("Printer not ready. Please check the printer and try again.");
        return;
      }
      setIsPrinting(true);
      try {
        await printOnSunmi(receiptPreviewRef.current, 1);
        onClose();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      setIsPrinting(false);
      return;
    }

    setIsPrinting(true);
    const barcodeScript = isRefund
      ? ""
      : `<script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></` + `script>`;

    const html = `<!DOCTYPE html><html><head>
      <title>${title} - ${isRefund ? returnNo : creditMemoNo}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        html,body{background:#fff;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px}
        .page{max-width:300px;margin:0 auto;padding:12px}
        .wm{color:rgba(0,0,0,0.55);-webkit-print-color-adjust:exact;print-color-adjust:exact}
        @media print{
          *{color:#000 !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .wm{color:rgba(0,0,0,0.55) !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          body{margin:0;padding:0}
          .page{padding:4px;max-width:100%}
        }
      </style>
    </head>
    <body>
      <div class="page">${buildReceiptHtml()}</div>
      ${barcodeScript}
      <script>
        window.onload = function() {
          if (typeof JsBarcode !== 'undefined') {
            document.querySelectorAll('canvas.barcode-receipt').forEach(function(el) {
              try { JsBarcode(el, '${creditMemoNo || "0000000000"}', {format:'CODE128',width:2,height:55,displayValue:false,margin:0,lineColor:'#000',background:'#fff'}); } catch(e){}
            });
          }
          setTimeout(function(){ window.print(); window.close(); }, 500);
        };
      </` + `script>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    setTimeout(() => {
      URL.revokeObjectURL(url);
      setIsPrinting(false);
      onClose();
    }, 2000);
    if (!win) setIsPrinting(false);
  };

  const blk: React.CSSProperties = { color: "#000" };
  const rule: React.CSSProperties = { borderBottom: "1px solid #999", margin: "2px 0" };
  const totalLabel: React.CSSProperties = { padding: "1px 0", paddingLeft: "70px", ...blk };
  const totalValue: React.CSSProperties = { textAlign: "right", padding: "1px 0", ...blk };

  const renderReceiptPreview = () => (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Arial', 'Helvetica Neue', sans-serif",
        fontSize: "11px",
        lineHeight: "1.3",
        color: "#000",
        paddingLeft: "8px",
        paddingRight: "8px",
      }}
    >
      {watermark && (
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%,-50%) rotate(-35deg)",
          fontSize: "38px", fontWeight: 900,
          color: "rgba(0,0,0,0.55)", whiteSpace: "nowrap",
          pointerEvents: "none", zIndex: 0, letterSpacing: "2px",
        }}>{watermark}</div>
      )}

      {/* Header */}
      <div style={{ textAlign: "center", margin: 0, paddingTop: "4px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img
            src={logoUrl ?? "/biznify-logo.png"}
            alt="Biznify"
            style={{ maxWidth: "100%", maxHeight: "80px", width: "auto", height: "auto", objectFit: "contain", display: "block" }}
          />
        </div>
        {outletName && (
          <div style={{ fontSize: "11px", fontWeight: "bold", marginTop: "2px", ...blk }}>{outletName}</div>
        )}
        {storeAddress && (
          <div style={{ fontSize: "9px", fontWeight: "bold", ...blk }}>{storeAddress}</div>
        )}
        {storePhone && (
          <div style={{ fontSize: "9px", ...blk }}>Phone : {storePhone}</div>
        )}
        <div style={{ fontSize: "12px", fontWeight: "bold", marginTop: "3px", ...blk }}>{title}</div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginTop: "2px", ...blk }}>
        <tbody>
          <tr>
            <td style={{ ...blk }}>{binNumber ? `BIN : ${binNumber}` : ""}</td>
          </tr>
        </tbody>
      </table>
      <div style={rule} />

      {/* Return / Customer info */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", ...blk }}>
        <tbody>
          <tr>
            <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Return No. : {returnNo}</td>
          </tr>
          {!isRefund && (
            <tr>
              <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Credit Memo No. : {creditMemoNo}</td>
            </tr>
          )}
          <tr>
            <td style={{ fontWeight: "bold", ...blk }}>Return Date : {dateShort}</td>
            <td style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: "bold", ...blk }}>{timeStr}</td>
          </tr>
          <tr>
            <td colSpan={2} style={{ ...blk }}>Name : {customerName}</td>
          </tr>
          <tr>
            <td colSpan={2} style={{ ...blk }}>Mobile : {customerMobile}</td>
          </tr>
          <tr>
            <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Served by : {cashier}</td>
          </tr>
        </tbody>
      </table>
      <div style={rule} />

      {/* Items table */}
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", ...blk }}>
        <colgroup>
          <col style={{ width: "16px" }} />
          <col />
          <col style={{ width: "36px" }} />
          <col style={{ width: "52px" }} />
          <col style={{ width: "56px" }} />
        </colgroup>
        <thead>
          <tr style={{ borderBottom: "1px solid #000" }}>
            {(["SL", "Description", "Rqty", "MRP", "Amount"] as const).map((h, i) => (
              <th
                key={h}
                style={{
                  textAlign: i === 0 || i === 1 ? "left" : i === 2 ? "center" : "right",
                  padding: "2px 2px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  ...blk,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {basketItems.map((item, idx) => (
            <tr key={idx}>
              <td style={{ padding: "2px 2px 2px 0", verticalAlign: "top", fontSize: "10px", ...blk }}>{idx + 1}</td>
              <td style={{ padding: "2px 2px", verticalAlign: "top", ...blk }}>
                <div style={{ fontSize: "10px", ...blk }}>{item.name}</div>
                {item.code && <div style={{ fontSize: "9px", ...blk }}>{item.code}</div>}
              </td>
              <td style={{ textAlign: "center", padding: "2px 2px", verticalAlign: "top", fontSize: "10px", ...blk }}>{item.qty.toFixed(3)}</td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap", padding: "2px 2px", verticalAlign: "top", fontSize: "10px", ...blk }}>{fmt(item.price)}</td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap", padding: "2px 0 2px 2px", verticalAlign: "top", fontSize: "10px", ...blk }}>{fmt(item.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "1px solid #000" }}>
            <td colSpan={2} style={{ padding: "2px 2px 2px 0", fontSize: "10px", fontWeight: "bold", ...blk }}>Sub Total :</td>
            <td style={{ textAlign: "center", padding: "2px 2px", fontSize: "10px", fontWeight: "bold", ...blk }}>{totalQty.toFixed(2)}</td>
            <td />
            <td style={{ textAlign: "right", padding: "2px 0 2px 2px", fontSize: "10px", fontWeight: "bold", ...blk }}>{fmt(subtotal)}</td>
          </tr>
        </tfoot>
      </table>
      <div style={rule} />

      {/* Totals */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", ...blk }}>
        <tbody>
          <tr>
            <td style={totalLabel}>Discount :</td>
            <td style={totalValue}>{fmt(discount)}</td>
          </tr>
          <tr>
            <td style={totalLabel}>VAT :</td>
            <td style={totalValue}>{fmt(tax)}</td>
          </tr>
          <tr>
            <td style={{ ...totalLabel, fontWeight: "bold" }}>Net Amount :</td>
            <td style={{ ...totalValue, fontWeight: "bold" }}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>

      {/* Amount in words */}
      <div style={{ fontSize: "10px", margin: "2px 0", ...blk }}>
        <b>In words:</b> <i>{amountInWordsTaka(total)}</i>
      </div>

      {/* Refund / Credit Info */}
      <div style={{ display: "inline-block", fontSize: "11px", fontWeight: "bold", margin: "2px 0 1px", paddingBottom: "1px", borderBottom: "1px solid #000", ...blk }}>
        {infoTitle}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", ...blk }}>
        <tbody>
          <tr style={{ borderBottom: "1px solid #000" }}>
            <td style={{ padding: "1px 0", ...blk }}>DESCRIPTION</td>
            <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>AMOUNT</td>
          </tr>
          <tr>
            <td style={{ padding: "1px 0", fontWeight: "bold", ...blk }}>{infoLabel}</td>
            <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>

      {/* Exchange only: terms + credit memo barcode for scanning at tender */}
      {!isRefund && (
        <>
          <div style={{ ...rule, margin: "4px 0 2px" }} />
          <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "1px", ...blk }}>Terms &amp; Conditions:</div>
          {terms.map((t) => (
            <div key={t} style={{ fontSize: "9px", ...blk }}>{t}</div>
          ))}
          <div style={{ textAlign: "center", margin: "6px 0 2px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <span data-barcode-img="">
              <Barcode
                value={creditMemoNo || "0000000000"}
                format="CODE128"
                width={1.5}
                height={55}
                displayValue={false}
                margin={6}
                lineColor="#000"
                background="#fff"
                renderer="canvas"
              />
            </span>
            <div style={{ fontSize: "9px", marginTop: "2px", ...blk }}>{creditMemoNo}</div>
          </div>
        </>
      )}

      {/* Footer */}
      <div style={{ textAlign: "center", fontSize: "10px", marginTop: "4px", lineHeight: "1.4", ...blk }}>
        <div style={{ fontWeight: "bold", ...blk }}>Thank you for shopping</div>
        <div style={{ fontWeight: "bold", ...blk }}>Customer Copy</div>
      </div>
      <div style={{ textAlign: "right", fontSize: "9px", marginTop: "4px", ...blk }}>Powered by biznify.net</div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">
              {isRefund ? "Refund" : "Exchange"} Successful
            </h2>
            <p className="text-sm text-gray-300">Print a return receipt or close</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Receipt Preview */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Receipt Preview</h3>
            <div ref={receiptPreviewRef} className="mx-auto" style={{ width: "302px" }}>
              <div className="bg-white">
                {renderReceiptPreview()}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold cursor-pointer transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className={`px-8 py-3 rounded-lg font-bold transition-colors flex items-center gap-2 ${
              isPrinting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700 text-white cursor-pointer"
            }`}
          >
            {isPrinting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Printing...
              </>
            ) : (
              <>
                <i className="ri-printer-line"></i>
                Print Receipt
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
